import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { ISimpleBarChartValue } from '@ui/BarChart';
import {
  getHBarWidthPercents,
  isPositiveChartBarValue,
} from '@ui/BarChart/helpers/bar-chart.helpers';
import { SimpleBarChartTooltip } from '@features/projects/charts/SimpleBarChartTooltip';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import {
  getLifecycleBarStripeBackground,
  getLifecycleBarStripePhase,
} from '@shared/helpers/display/lifecycle-bar-stripe.helpers';

export type HorizontalResultSegment = ISimpleBarChartValue & {
  color?: string;
  tooltip?: React.ReactNode;
};

const HorizontalBarSegmentTooltip = ({
  segment,
  children,
}: {
  segment: HorizontalResultSegment;
  children: React.ReactElement;
}) =>
  segment.tooltip !== undefined ? (
    <Tooltip title={segment.tooltip} disableInteractive>
      {children}
    </Tooltip>
  ) : (
    <SimpleBarChartTooltip
      disableInteractive
      label={segment.tooltipData?.label}
      co2e={segment.tooltipData?.co2e}
      sek={segment.tooltipData?.sek}
    >
      {children}
    </SimpleBarChartTooltip>
  );

export const GROUP_RESULT_BAR_HEIGHT = 24;

/** Shared horizontal climate/cost bar used by Build rows and project rows. */
export const HorizontalResultBar: React.FC<{
  co2Value: number;
  costValue: number;
  color: string;
  maxCo2: number;
  maxCost: number;
  barHeight?: number;
  costBarHeight?: number;
  co2Segments?: ISimpleBarChartValue[];
  segments?: HorizontalResultSegment[];
  baselineCo2Value?: number;
  baselineColor?: string;
  rightCornerRadius?: number;
  outlineSegments?: boolean;
  dimOtherSegmentsOnHover?: boolean;
  hideCo2e?: boolean;
  hideCost?: boolean;
}> = ({
  co2Value,
  costValue,
  color,
  maxCo2,
  maxCost,
  barHeight = 4,
  costBarHeight = barHeight >= 12 ? 2 : 1,
  co2Segments,
  segments,
  baselineCo2Value,
  baselineColor = BASELINE_COLOR,
  rightCornerRadius = 1,
  outlineSegments = false,
  dimOtherSegmentsOnHover = true,
  hideCo2e = false,
  hideCost = false,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const { co2Pct, costPct, baselinePct } = getHBarWidthPercents({
    co2Value,
    costValue,
    maxCo2,
    maxCost,
    baselineCo2Value,
  });
  const activeSegments: HorizontalResultSegment[] =
    (segments ?? co2Segments)?.filter((s) =>
      isPositiveChartBarValue(s.value),
    ) ?? [];
  const hasSegments =
    activeSegments.length > 0 && isPositiveChartBarValue(co2Value);
  // Animate width/color only when an active proposal is shown; snap to baseline immediately.
  // Note: `none` is only valid as the entire `transition` value, not as a per-property
  // duration/easing, so each string must be a full `transition` declaration.
  const isProposalActive = color !== BASELINE_COLOR;
  const barCo2Transition = isProposalActive
    ? 'width 1s ease, background-color 1s ease'
    : 'none';
  const barCostTransition = isProposalActive ? 'width 1s ease' : 'none';
  const segmentTransition = isProposalActive
    ? 'opacity 0.15s ease, width 1s ease, background-color 1s ease'
    : 'opacity 0.15s ease';

  return (
    <div
      className="hbar-root"
      style={{
        position: 'relative',
        height: `${barHeight}px`,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {!hideCo2e && baselineCo2Value != null && (
        <div
          className="hbar-baseline"
          style={{
            position: 'absolute',
            inset: 0,
            width: `${baselinePct}%`,
            borderRadius: `0 ${rightCornerRadius}px ${rightCornerRadius}px 0`,
            backgroundColor: baselineColor,
            transition: barCo2Transition,
          }}
        />
      )}
      {!hideCo2e && (
        <div
          className="hbar-co2"
          style={{
            position: 'absolute',
            inset: 0,
            width: `${co2Pct}%`,
            borderRadius: `0 ${rightCornerRadius}px ${rightCornerRadius}px 0`,
            backgroundColor: hasSegments ? 'transparent' : color,
            display: 'flex',
            overflow: 'hidden',
            transition: barCo2Transition,
          }}
        >
          {hasSegments &&
            activeSegments.map((seg, i) => {
              const segWidthPct = (seg.value / co2Value) * 100;
              const stripePhase = segments
                ? undefined
                : getLifecycleBarStripePhase(seg.key);
              return (
                <HorizontalBarSegmentTooltip key={seg.key ?? i} segment={seg}>
                  <div
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(-1)}
                    style={{
                      width: `${segWidthPct}%`,
                      height: '100%',
                      flexShrink: 0,
                      position: 'relative',
                      backgroundColor: seg.color ?? color,
                      ...(outlineSegments &&
                        i < activeSegments.length - 1 && {
                          boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.1)',
                        }),
                      opacity:
                        dimOtherSegmentsOnHover &&
                        hoveredIndex >= 0 &&
                        hoveredIndex !== i
                          ? 0.2
                          : 1,
                      transition: segmentTransition,
                    }}
                  >
                    {stripePhase && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage:
                            getLifecycleBarStripeBackground(stripePhase),
                          opacity: 0.8,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </div>
                </HorizontalBarSegmentTooltip>
              );
            })}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}
      {!hideCost && (
        <div
          className="hbar-cost"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: `${costBarHeight}px`,
            width: `${costPct}%`,
            backgroundColor: 'black',
            opacity: 0.6,
            transition: barCostTransition,
          }}
        />
      )}
    </div>
  );
};
